'use client'
import {
  useQueue_default
} from "./chunk-XG65SW5G.mjs";

// src/components/HideOnQueue.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var HideOnQueue = ({ children }) => {
  const { isActive } = useQueue_default();
  if (isActive) return null;
  return /* @__PURE__ */ jsx(Fragment, { children });
};

// src/components/QueueView.tsx
import { twMerge } from "tailwind-merge";
import { formatPrice } from "@wbk/utils";
import { useRouter, useTranslation } from "@wbk/config";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var QueueView = ({ className = "", style }) => {
  const { locale } = useRouter();
  const { queue, isActive } = useQueue_default({ autoRefresh: true });
  const { t } = useTranslation("queue_common");
  if (!queue || !isActive) return null;
  const percentage = queue.waiting_number / queue.total_in_queue * 100;
  return /* @__PURE__ */ jsxs("div", { className: twMerge("space-y-4", className), style, children: [
    /* @__PURE__ */ jsx2("p", { className: "mb-2 text-xl font-bold", children: t("queue_common:queue_title") }),
    /* @__PURE__ */ jsxs("div", { className: "bg-paper rounded-S p-M border-paper border", children: [
      /* @__PURE__ */ jsx2("h2", { children: formatPrice(Math.max((queue == null ? void 0 : queue.waiting_number) || 0, 1), 0) }),
      /* @__PURE__ */ jsx2("p", { className: "text-text-secondary text-sm", children: t("queue_common:queue_position") }),
      /* @__PURE__ */ jsx2("div", { className: "bg-paper-level-1 relative my-2 h-10 w-full rounded-full", children: /* @__PURE__ */ jsx2(
        "div",
        {
          className: "bg-tertiary after:bg-candy-stripes after:ltr:animate-candy-stripes-ltr after:rtl:animate-candy-stripes-rtl absolute top-1/2 h-8 min-w-[80px] -translate-y-1/2 overflow-hidden rounded-full after:absolute after:inset-0 after:z-10 after:bg-[length:75px_75px] ltr:left-1 rtl:right-1",
          style: { width: `${100 - Math.min(percentage, 99)}%` }
        }
      ) }),
      /* @__PURE__ */ jsx2("p", { className: "text-text-secondary text-sm", children: t("queue_common:queue_p1") }),
      (queue == null ? void 0 : queue.message_ar) || (queue == null ? void 0 : queue.message_en) ? /* @__PURE__ */ jsx2("p", { children: (locale == null ? void 0 : locale.startsWith("ar")) ? queue == null ? void 0 : queue.message_ar : queue == null ? void 0 : queue.message_en }) : null
    ] }),
    /* @__PURE__ */ jsx2("p", { className: "text-text-secondary text-sm", children: t("queue_common:queue_p2") })
  ] });
};

// src/components/QueueViewBar.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { formatPrice as formatPrice2 } from "@wbk/utils";
import { useTranslation as useTranslation2 } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var getInitials = (user) => {
  var _a, _b, _c, _d;
  const first = ((_b = (_a = user == null ? void 0 : user.first_name) == null ? void 0 : _a.trim()) == null ? void 0 : _b[0]) || "";
  const last = ((_d = (_c = user == null ? void 0 : user.last_name) == null ? void 0 : _c.trim()) == null ? void 0 : _d[0]) || "";
  return `${first}${last}`.toUpperCase();
};
var QueueViewBar = ({ user, className = "" }) => {
  const { queue, isActive } = useQueue_default({ autoRefresh: true });
  const { t } = useTranslation2("queue_common");
  if (!queue || !isActive) return null;
  const percentage = queue.waiting_number / queue.total_in_queue * 100;
  const progress = 100 - Math.min(percentage, 99);
  const initials = getInitials(user);
  return /* @__PURE__ */ jsxs2("div", { className: twMerge2("bg-paper border-paper rounded-M space-y-2 border p-4", className), children: [
    /* @__PURE__ */ jsxs2("p", { className: "gap-XS flex flex-wrap items-baseline", children: [
      /* @__PURE__ */ jsx3("span", { className: "text-xl font-bold", children: formatPrice2(Math.max((queue == null ? void 0 : queue.waiting_number) || 0, 1), 0) }),
      /* @__PURE__ */ jsx3("span", { className: "text-text-secondary text-sm", children: t("queue_common:queue_position") })
    ] }),
    /* @__PURE__ */ jsxs2("div", { className: "bg-paper-level-3 relative h-4 w-full rounded-full", children: [
      /* @__PURE__ */ jsx3(
        "div",
        {
          className: "after:bg-candy-stripes after:ltr:animate-candy-stripes-ltr after:rtl:animate-candy-stripes-rtl absolute top-1/2 h-full min-w-[80px] -translate-y-1/2 overflow-hidden rounded-full bg-[#4A79FF] after:absolute after:inset-0 after:z-10 after:bg-[length:75px_75px]",
          style: { width: `${progress}%` }
        }
      ),
      /* @__PURE__ */ jsx3(
        "div",
        {
          className: "pointer-events-none absolute top-1/2 z-20 h-full min-w-[80px] -translate-y-1/2 ltr:left-0 rtl:right-0",
          style: { width: `${progress}%` },
          children: /* @__PURE__ */ jsx3("span", { className: "bg-primary border-paper absolute top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border-2 text-xs font-semibold ltr:-right-0.5 rtl:-left-0.5", children: initials })
        }
      ),
      /* @__PURE__ */ jsx3("span", { className: "text-tertiary-contrast absolute top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-[#4A79FF] ltr:right-0 rtl:left-0", children: /* @__PURE__ */ jsx3(TicketGlyph, {}) })
    ] })
  ] });
};
var TicketGlyph = () => /* @__PURE__ */ jsx3("svg", { viewBox: "0 0 24 24", className: "h-4 w-4", fill: "#ffffff", "aria-hidden": "true", children: /* @__PURE__ */ jsx3("path", { d: "M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6Z" }) });

export {
  HideOnQueue,
  QueueView,
  QueueViewBar
};
//# sourceMappingURL=chunk-5YQRWZBO.mjs.map